import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaTimes, FaStar } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import { useAppSelector } from '../../store/hooks';
import { obterIdTenent } from '../../services/seguranca';
import { IdEmpresaAdm } from '../../services/utilitarios';

const GOOGLE_REVIEW_URL = 'https://g.page/r/Ca44y71hSKQFEAI/review';
const STORAGE_KEY = 'google_review_prompt_v1';
const SNOOZE_DIAS = 3;
const MAX_EXIBICOES = 5;
const DELAY_EXIBICAO_MS = 8000;

interface ReviewPromptState {
  done: boolean;
  snoozeUntil: number;
  count: number;
}

const lerEstado = (): ReviewPromptState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* estado corrompido, recomeça */ }
  return { done: false, snoozeUntil: 0, count: 0 };
};

const salvarEstado = (estado: ReviewPromptState) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(estado));
};

// ======== ANIMATIONS ========
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.9) translateY(16px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;
const starPop = keyframes`
  0% { transform: scale(0) rotate(-30deg); opacity: 0; }
  60% { transform: scale(1.25) rotate(8deg); opacity: 1; }
  100% { transform: scale(1) rotate(0); opacity: 1; }
`;
const shine = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(66, 133, 244, 0.45); }
  70% { box-shadow: 0 0 0 14px rgba(66, 133, 244, 0); }
  100% { box-shadow: 0 0 0 0 rgba(66, 133, 244, 0); }
`;

// ======== LAYOUT ========
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1056;
  padding: 20px;
  animation: ${fadeIn} 0.25s ease-out;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.28);
  animation: ${scaleIn} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
  position: relative;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #1F467E 0%, #2d5fa8 60%, #3b82f6 100%);
  padding: 32px 24px 26px;
  text-align: center;
  color: #fff;
  position: relative;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.15s;

  &:hover { background: rgba(255, 255, 255, 0.3); }
`;

const GoogleBadge = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.1rem;
  margin: 0 auto 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
`;

const Titulo = styled.h3`
  font-size: 1.25rem;
  font-weight: 800;
  margin: 0 0 6px;
  background: linear-gradient(90deg, #fff 40%, #ffd76e 50%, #fff 60%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shine} 3.5s linear infinite;
`;

const Subtitulo = styled.p`
  font-size: 0.85rem;
  margin: 0;
  color: #fff !important;
  line-height: 1.45;
`;

const Body = styled.div`
  padding: 24px;
  text-align: center;
`;

const StarsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const StarWrap = styled.button<{ delay: number; hovered: boolean }>`
  border: none;
  background: none;
  padding: 2px;
  cursor: pointer;
  font-size: 2.4rem;
  line-height: 1;
  color: ${p => p.hovered ? '#f59e0b' : '#e2e8f0'};
  animation: ${starPop} 0.45s ease-out both;
  animation-delay: ${p => p.delay}ms;
  transition: color 0.12s, transform 0.12s;

  &:hover { transform: scale(1.2); }
`;

const StarsHint = styled.p`
  font-size: 0.78rem;
  color: #94a3b8;
  margin: 0 0 18px;
`;

const Mensagem = styled.p`
  font-size: 0.9rem;
  color: #475569;
  line-height: 1.55;
  margin: 0 0 20px;

  strong { color: #1F467E; }
`;

const BotaoGoogle = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  background: #4285F4;
  color: #fff !important;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none !important;
  transition: all 0.15s;
  animation: ${pulse} 2.2s infinite;

  &:hover {
    background: #3367d6;
    transform: translateY(-2px);
  }
`;

const BotaoGoogleIcone = styled.span`
  background: #fff;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
`;

const Acoes = styled.div`
  display: flex;
  justify-content: center;
  gap: 18px;
  margin-top: 16px;
`;

const LinkSecundario = styled.button`
  border: none;
  background: none;
  font-size: 0.78rem;
  color: #94a3b8;
  cursor: pointer;
  text-decoration: underline;
  padding: 4px;

  &:hover { color: #64748b; }
`;

// ======== COMPONENT ========
const GoogleReviewModal: React.FC = () => {
  const token = useAppSelector(state => state.token);
  const logado = useAppSelector(state => state.logado);
  const tenantId = obterIdTenent(token);

  const [show, setShow] = useState(false);
  const [hoverStar, setHoverStar] = useState(0);

  useEffect(() => {
    if (!logado || !token || tenantId === IdEmpresaAdm) return;

    const estado = lerEstado();
    if (estado.done || estado.count >= MAX_EXIBICOES) return;
    if (estado.snoozeUntil && Date.now() < estado.snoozeUntil) return;

    const timeoutId = setTimeout(() => {
      // Nao concorre com a pesquisa de satisfacao se ela estiver aberta
      if (document.getElementById('pesquisa-overlay')) return;
      salvarEstado({ ...estado, count: estado.count + 1 });
      setShow(true);
    }, DELAY_EXIBICAO_MS);

    return () => clearTimeout(timeoutId);
  }, [logado, token, tenantId]);

  const marcarConcluido = useCallback(() => {
    salvarEstado({ ...lerEstado(), done: true });
    setShow(false);
  }, []);

  const adiar = useCallback(() => {
    salvarEstado({ ...lerEstado(), snoozeUntil: Date.now() + SNOOZE_DIAS * 24 * 60 * 60 * 1000 });
    setShow(false);
  }, []);

  const abrirReview = useCallback(() => {
    window.open(GOOGLE_REVIEW_URL, '_blank', 'noopener');
    marcarConcluido();
  }, [marcarConcluido]);

  if (!show) return null;

  return (
    <Overlay data-modal-inicial onClick={adiar}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <CloseBtn onClick={adiar}><FaTimes /></CloseBtn>
          <GoogleBadge><FcGoogle /></GoogleBadge>
          <Titulo>Sua opinião vale ouro!</Titulo>
          <Subtitulo>
            Ajude outras locadoras a conhecerem o LocaSmartPro
          </Subtitulo>
        </Header>

        <Body>
          <StarsRow>
            {[1, 2, 3, 4, 5].map((s, i) => (
              <StarWrap
                key={s}
                delay={150 + i * 110}
                hovered={hoverStar >= s}
                onMouseEnter={() => setHoverStar(s)}
                onMouseLeave={() => setHoverStar(0)}
                onClick={abrirReview}
                aria-label={`Avaliar com ${s} estrelas no Google`}
              >
                <FaStar />
              </StarWrap>
            ))}
          </StarsRow>
          <StarsHint>Toque nas estrelas para avaliar</StarsHint>

          <Mensagem>
            Leva <strong>menos de 1 minuto</strong> e faz uma diferença enorme
            para nós. Conte como o LocaSmartPro tem ajudado na gestão da sua locadora!
          </Mensagem>

          <BotaoGoogle href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" onClick={marcarConcluido}>
            <BotaoGoogleIcone><FcGoogle /></BotaoGoogleIcone>
            Avaliar no Google
          </BotaoGoogle>

          <Acoes>
            <LinkSecundario onClick={marcarConcluido}>Já avaliei</LinkSecundario>
            <LinkSecundario onClick={adiar}>Agora não</LinkSecundario>
          </Acoes>
        </Body>
      </Modal>
    </Overlay>
  );
};

export default GoogleReviewModal;
